// apps/web/src/lib/barcode.ts
import type { NormalizedProduct, Per100g } from "@shared/types";
import { type Micros, scaleMicros } from "@shared/micros";

const CODE_RE = /^[0-9]{8,13}$/;

export function normalizeCode(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed || !/^[0-9]+$/.test(trimmed)) return null;
  const padded = trimmed.length === 12 ? "0" + trimmed : trimmed;
  return CODE_RE.test(padded) ? padded : null;
}

const round1 = (n: number | null): number | null =>
  n === null ? null : Math.round(n * 10) / 10;

export interface RecomputedMacros {
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g: number | null;
  fiber_g: number | null;
  micros: Micros | null;
}

export function recomputeMacros(per100: Per100g, amountG: number): RecomputedMacros {
  const f = amountG / 100;
  return {
    calories:  Math.round(per100.kcal * f),
    protein_g: round1(per100.protein_g === null ? null : per100.protein_g * f),
    carbs_g:   round1(per100.carbs_g   === null ? null : per100.carbs_g   * f),
    fat_g:     round1(per100.fat_g     === null ? null : per100.fat_g     * f),
    sugar_g:   round1(per100.sugar_g   === null ? null : per100.sugar_g   * f),
    fiber_g:   round1(per100.fiber_g   === null ? null : per100.fiber_g   * f),
    micros:    scaleMicros(per100.micros, f),
  };
}

export function formatEntryName(p: NormalizedProduct): string {
  return p.brand ? `${p.brand} · ${p.name}` : p.name;
}

export interface EnteredTotals {
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g?: number | null;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const scale1 = (v: number | null, f: number): number | null =>
  v === null ? null : clamp(Math.round(v * f * 10) / 10, 0, 100);

/** Inverse of recomputeMacros: turn an eaten portion's totals into per-100g,
 *  clamped to the ranges the write-back endpoint accepts. */
export function derivePer100g(totals: EnteredTotals, amountG: number): Per100g {
  const f = amountG > 0 ? 100 / amountG : 1;
  return {
    kcal: clamp(Math.round(totals.calories * f), 0, 2000),
    protein_g: scale1(totals.protein_g, f),
    carbs_g: scale1(totals.carbs_g, f),
    sugar_g: scale1(totals.sugar_g ?? null, f),
    fat_g: scale1(totals.fat_g, f),
    sat_fat_g: null,
    fiber_g: null,
    salt_g: null,
    micros: null,
  };
}

// Lazy ZXing loader. Only invoked from BarcodeScannerSheet.
let _zxingPromise: Promise<typeof import("@zxing/browser")> | null = null;
export function loadZXing() {
  if (!_zxingPromise) _zxingPromise = import("@zxing/browser");
  return _zxingPromise;
}

export function canScanBarcode(): boolean {
  return typeof navigator !== "undefined"
    && "mediaDevices" in navigator
    && typeof navigator.mediaDevices?.getUserMedia === "function";
}
